/**
 * <s-vault-gate>: runs the opening countdown and the "Have the code?" unlock.
 * The code is checked against the hash of the Vault password; a match is
 * remembered in this browser and reveals every [data-vault-content] region.
 * Changing the password changes the hash, so old unlocks stop working.
 * The email sign-up is a normal form and works on its own.
 */
class SVaultGate extends HTMLElement {
  connectedCallback() {
    this.hash = this.dataset.hash || '';
    this.storageKey = `disciples-vault-${this.hash.slice(0, 16)}`;
    this.unlockForm = this.querySelector('[data-vault-unlock]');
    this.codeInput = this.querySelector('[data-vault-code]');
    this.error = this.querySelector('[data-vault-error]');

    if (this.isUnlocked()) this.reveal(false);

    this.unlockForm?.addEventListener('submit', (event) => this.onUnlock(event));
    this.codeInput?.addEventListener('input', () => {
      if (this.error) this.error.hidden = true;
      this.codeInput.removeAttribute('aria-invalid');
    });
    document.querySelectorAll('[data-vault-lock]').forEach((button) => {
      button.addEventListener('click', () => this.lock());
    });

    this.notifyForm = this.querySelector('form#vault-notify');
    this.notifyForm?.addEventListener('submit', (event) => this.onNotify(event));

    this.startCountdown();
  }

  // Sends the sign-up in the background and shows Shopify's confirmation (or errors) in place,
  // so the shopper stays on The Vault. Falls back to a normal submit if anything is unexpected,
  // such as a spam-check page.
  async onNotify(event) {
    if (this.notifySubmitting === 'native') return;
    event.preventDefault();
    const form = this.notifyForm;
    if (!form.reportValidity() || this.notifySubmitting) return;
    this.notifySubmitting = 'fetch';
    const button = form.querySelector('[type="submit"]');
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;

    const fallback = () => {
      this.notifySubmitting = 'native';
      if (button) button.disabled = false;
      form.submit();
    };

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok || response.url.includes('challenge')) return fallback();

      const html = await response.text();
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('form#vault-notify');
      if (!fresh) return fallback();

      form.innerHTML = fresh.innerHTML;
      this.notifySubmitting = null;
      const message = form.querySelector('[role="status"], [role="alert"]');
      message?.setAttribute('tabindex', '-1');
      message?.focus();
    } catch {
      fallback();
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  startCountdown() {
    const countdown = this.querySelector('[data-vault-countdown]');
    if (!countdown) return;
    const openAt = new Date(countdown.dataset.open).getTime();
    if (Number.isNaN(openAt)) {
      countdown.hidden = true;
      return;
    }
    const cells = {};
    countdown.querySelectorAll('[data-countdown]').forEach((cell) => (cells[cell.dataset.countdown] = cell));
    const pad = (value) => String(value).padStart(2, '0');

    const update = () => {
      const remaining = Math.max(0, openAt - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      if (cells.days) cells.days.textContent = pad(Math.floor(totalSeconds / 86400));
      if (cells.hours) cells.hours.textContent = pad(Math.floor((totalSeconds % 86400) / 3600));
      if (cells.minutes) cells.minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
      if (cells.seconds) cells.seconds.textContent = pad(totalSeconds % 60);
      if (remaining === 0) {
        clearInterval(this.timer);
        countdown.querySelector('[data-countdown-label]')?.classList.add('hidden');
        countdown.querySelector('[data-countdown-cells]')?.classList.add('hidden');
        countdown.querySelector('[data-countdown-open]')?.classList.remove('hidden');
      }
    };
    update();
    this.timer = setInterval(update, 1000);
  }

  isUnlocked() {
    try {
      return Boolean(this.hash) && localStorage.getItem(this.storageKey) === '1';
    } catch {
      return false;
    }
  }

  async sha256(text) {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  async onUnlock(event) {
    event.preventDefault();
    const code = this.codeInput.value.trim();
    const matches = code !== '' && this.hash !== '' && (await this.sha256(code)) === this.hash;

    if (!matches) {
      if (this.error) this.error.hidden = false;
      this.codeInput.setAttribute('aria-invalid', 'true');
      this.setAttribute('data-shake', '');
      setTimeout(() => this.removeAttribute('data-shake'), 450);
      this.codeInput.focus();
      return;
    }

    try {
      localStorage.setItem(this.storageKey, '1');
    } catch {
      /* Private browsing: the vault opens for this visit only. */
    }
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.setAttribute('data-unlocking', '');
    setTimeout(() => this.reveal(true), reduce ? 0 : 800);
  }

  reveal(focus = true) {
    const gate = this.querySelector('[data-vault-gate]');
    if (gate) gate.hidden = true;
    const regions = document.querySelectorAll('[data-vault-content]');
    regions.forEach((region) => (region.hidden = false));
    if (focus) {
      window.scrollTo({ top: 0 });
      regions[0]?.querySelector('h1, h2')?.focus();
    }
  }

  lock() {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      /* nothing stored */
    }
    window.location.reload();
  }
}

if (!customElements.get('s-vault-gate')) customElements.define('s-vault-gate', SVaultGate);
